import { useState } from 'react';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import { ProductResponse } from '../types/api-responses';

interface ProductOption {
    value: string;
    label: string;
}

interface UseProductManagementParams {
    apiUrl: string;
    apiKey: string;
    setProducts: (products: ProductOption[]) => void;
    onProductCreated?: (product: { uuid: string; name: string }) => void;
}

export const useProductManagement = ({ apiUrl, apiKey, setProducts, onProductCreated }: UseProductManagementParams) => {
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [isCreatingProduct, setIsCreatingProduct] = useState(false);
    const [newProduct, setNewProduct] = useState({ name: '', description: '', quality_standard: '' });

    const handleCreateProduct = async () => {
        if (!newProduct.name.trim()) {
            toast.error('Nome é obrigatório');
            return;
        }

        try {
            setIsCreatingProduct(true);
            const token = (await Auth.currentSession()).getIdToken().getJwtToken();

            const response = await fetch(`${apiUrl}/product`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': apiKey,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: newProduct.name,
                    description: newProduct.description,
                    quality_standard: newProduct.quality_standard
                })
            });

            if (response.ok) {
                const createdProduct = await response.json();
                toast.success('Produto criado com sucesso!');
                setIsProductModalOpen(false);
                setNewProduct({ name: '', description: '', quality_standard: '' });

                const token2 = (await Auth.currentSession()).getIdToken().getJwtToken();
                const productsResponse = await fetch(`${apiUrl}/product`, {
                    headers: {
                        'Authorization': `Bearer ${token2}`,
                        'x-api-key': apiKey
                    }
                });
                if (productsResponse.ok) {
                    const data = await productsResponse.json();
                    const productsList = data.products || data;
                    setProducts(productsList.map((product: ProductResponse) => ({
                        value: product.uuid,
                        label: product.name
                    })));

                    if (createdProduct?.uuid) {
                        onProductCreated?.(createdProduct);
                    }
                }
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao criar produto');
            }
        } catch {
            toast.error('Erro ao conectar com o servidor ao criar produto');
        } finally {
            setIsCreatingProduct(false);
        }
    };

    return {
        isProductModalOpen,
        setIsProductModalOpen,
        isCreatingProduct,
        newProduct,
        setNewProduct,
        handleCreateProduct
    };
};
